// Event sessions (workshop_session, server V101). The contract is
// zebra-portal-server docs/events-types-sessions-contract.md section B:
// GET/PUT /api/workshops/sessions/:workshopId send and receive the whole
// schedule (dates YYYY-MM-DD, times HH:MM). The weekly pattern is not stored;
// the generator here is portal UI state that writes rows into the PUT body.
import moment from "moment";

// Monday first, as staff read a week. `day` is moment's (0 = Sunday).
export const WEEKDAYS = [
  { day: 1, label: "Monday", short: "Mon" },
  { day: 2, label: "Tuesday", short: "Tue" },
  { day: 3, label: "Wednesday", short: "Wed" },
  { day: 4, label: "Thursday", short: "Thu" },
  { day: 5, label: "Friday", short: "Fri" },
  { day: 6, label: "Saturday", short: "Sat" },
  { day: 0, label: "Sunday", short: "Sun" },
];

// "18:00:00" / "18:00" -> "18:00"; "" when it isn't a time.
export const toHHMM = (time) => {
  const m = /^(\d{2}):(\d{2})/.exec(String(time || ""));
  return m ? `${m[1]}:${m[2]}` : "";
};

// The event's own date as YYYY-MM-DD, from the edit endpoint's value (a
// "YYYY-MM-DDT…" timestamp, as EventEditForm reads it).
export const toIsoDate = (value) => String(value || "").split("T")[0];

let lastUid = 0;
const nextUid = () => `s${++lastUid}`;

// GET results -> editor rows, plus a client-only _uid for React keys (a new
// session has no workshop_session_id yet).
export const sessionsToEditor = (sessions) =>
  (sessions || []).map((s) => ({
    _uid: nextUid(),
    workshop_session_id: s.workshop_session_id,
    session_date: toIsoDate(s.session_date),
    start_time: toHHMM(s.start_time),
    end_time: toHHMM(s.end_time),
    cancelled_ind: Number(s.cancelled_ind) === 1 ? 1 : 0,
    note: s.note || "",
    attendance_count: Number(s.attendance_count) || 0,
  }));

export const newEditorSession = (session_date, start_time, end_time) => ({
  _uid: nextUid(),
  session_date,
  start_time,
  end_time,
  cancelled_ind: 0,
  note: "",
  attendance_count: 0,
});

const bySlot = (a, b) =>
  a.session_date.localeCompare(b.session_date) || a.start_time.localeCompare(b.start_time);

export const sortSessions = (rows) => [...rows].sort(bySlot);

// Editor rows -> the PUT body: by date and time, a new session without an id.
export const buildSessionsPutBody = (rows) => ({
  sessions: sortSessions(rows).map((s) => ({
    ...(s.workshop_session_id ? { workshop_session_id: s.workshop_session_id } : {}),
    session_date: s.session_date,
    start_time: s.start_time,
    end_time: s.end_time,
    cancelled_ind: s.cancelled_ind ? 1 : 0,
    note: s.note.trim() || null,
  })),
});

// The generator's rows: every date in [from, to] on a ticked weekday, at
// that weekday's times, except the skip dates. times: { [day]: { start, end } }
// for the ticked weekdays only.
export function generateSessions({ times, from, to, skip = [] }) {
  const skipped = new Set(skip);
  const rows = [];
  const end = moment(to, "YYYY-MM-DD");
  for (let d = moment(from, "YYYY-MM-DD"); d.isSameOrBefore(end, "day"); d.add(1, "day")) {
    const t = times[d.day()];
    const date = d.format("YYYY-MM-DD");
    if (t && !skipped.has(date)) rows.push(newEditorSession(date, t.start, t.end));
  }
  return rows;
}

// The generator's times for generateSessions. With one time pair for all
// days (perDay false) every ticked day gets `shared`; with "Different times
// per day" each keeps its own. times: { [day]: { start, end } } for the
// ticked weekdays only; shared: { start, end }.
export const generatorTimes = ({ times, shared, perDay }) =>
  perDay
    ? times
    : Object.fromEntries(Object.keys(times).map((day) => [day, { ...shared }]));

const isIsoDate = (date) => moment(date, "YYYY-MM-DD", true).isValid();

// "Monday", "Monday and Wednesday", "Monday, Wednesday and Friday"
const joinWords = (words, last = "and") =>
  words.length < 2
    ? words.join("")
    : `${words.slice(0, -1).join(", ")} ${last} ${words[words.length - 1]}`;

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// "6:00 PM to 7:30 PM"; "" until both times are entered.
const timeRange = (t) =>
  t.start && t.end
    ? `${moment(t.start, "HH:mm").format("h:mm A")} to ${moment(t.end, "HH:mm").format("h:mm A")}`
    : "";

// The generator's plain-English preview, from its current inputs: e.g.
// "12 sessions: Monday and Wednesday, 6:00 PM to 7:30 PM, Jan 8 to Mar 26,
// skipping 2 dates", or with times that differ by day "12 sessions, Jan 8 to
// Mar 26: Monday 6:00 PM to 7:30 PM; Saturday 10:00 AM to 12:00 PM". The
// count is generateSessions' own, and only the skip dates that fall on a
// ticked weekday in range are counted. times: as generatorTimes gives them;
// range: the event's { startdate, enddate }; regenerate: { rows, today } when
// the table already has sessions, so the preview says what Regenerate
// changes (regenerateSessions) instead.
// -> { count, sentence }: count is the sessions Generate makes, or the ones
// Regenerate adds.
export function describeGenerator(
  { times, from, to, skip = [] },
  { startdate, enddate },
  regenerate = null
) {
  const ticked = WEEKDAYS.filter((w) => times[w.day]);
  if (ticked.length === 0)
    return {
      count: 0,
      sentence: regenerate ? "Tick a weekday to regenerate" : "No sessions yet: tick a weekday",
    };
  if (!isIsoDate(from) || !isIsoDate(to))
    return {
      count: 0,
      sentence: regenerate
        ? "Enter the first and last dates to regenerate"
        : "No sessions yet: enter the first and last dates",
    };
  if (from > to) return { count: 0, sentence: "No sessions: the first date is after the last" };
  // Refused by the generator anyway; this also keeps a half-typed year
  // (0002-…) from walking centuries of days.
  if (from < startdate || to > enddate)
    return { count: 0, sentence: "No sessions: keep the dates inside the event's" };

  const generated = generateSessions({ times, from, to, skip });
  const dateFormat = from.slice(0, 4) === to.slice(0, 4) ? "MMM D" : "MMM D, YYYY";
  const dateText = (d) => moment(d, "YYYY-MM-DD").format(dateFormat);

  if (regenerate) {
    const { rows, today } = regenerate;
    const kept = new Set(rows.filter((r) => isKeptOnRegenerate(r, today)));
    const added = regenerateSessions(rows, generated, today).rows.filter((r) => !kept.has(r));
    const removed = rows.length - kept.size;
    return {
      count: added.length,
      sentence:
        "Regenerate: " +
        (added.length
          ? `${plural(added.length, "session")} added from ${dateText(added[0].session_date)}`
          : "no sessions added") +
        (removed ? `, ${removed} removed` : "") +
        (kept.size ? `; ${plural(kept.size, "earlier, cancelled or marked session")} kept` : ""),
    };
  }

  const count = generated.length;
  const dates = from === to ? `on ${dateText(from)}` : `${dateText(from)} to ${dateText(to)}`;
  const skipped = skip.filter(
    (d) => d >= from && d <= to && times[moment(d, "YYYY-MM-DD").day()]
  ).length;
  const skipping = skipped ? `, skipping ${plural(skipped, "date")}` : "";
  if (count === 0)
    return {
      count,
      sentence: `No sessions: no ${joinWords(
        ticked.map((w) => w.label),
        "or"
      )} ${from === to ? dates : `from ${dates}`}${skipping}`,
    };
  // The ticked days grouped by their times, in week order.
  const groups = [];
  ticked.forEach((w) => {
    const range = timeRange(times[w.day]);
    const group = groups.find((g) => g.range === range);
    if (group) group.labels.push(w.label);
    else groups.push({ range, labels: [w.label] });
  });
  const groupText = (g) => [joinWords(g.labels), g.range].filter(Boolean).join(" ");
  if (groups.length === 1)
    return {
      count,
      sentence: `${plural(count, "session")}: ${[joinWords(groups[0].labels), groups[0].range]
        .filter(Boolean)
        .join(", ")}, ${dates}${skipping}`,
    };
  return {
    count,
    sentence: `${plural(count, "session")}, ${dates}${skipping}: ${groups
      .map(groupText)
      .join("; ")}`,
  };
}

// A session Regenerate keeps as it is: one before today, one with attendance
// marks (the server refuses to delete those), or a cancelled one (its note
// and its being cancelled are staff's decision, not the pattern's).
export const isKeptOnRegenerate = (row, today) =>
  row.session_date < today || row.attendance_count > 0 || !!row.cancelled_ind;

// Regenerate: the kept rows, plus the generated rows from today on, except on
// a date that already has a kept session from today on: a cancelled one (its
// date counts as a skip date) or one with attendance (a second session that
// day would only be a duplicate at the new time).
// -> { rows, cancelledDates, markedDates } (the dates left alone, for the
// confirm).
export function regenerateSessions(rows, generated, today) {
  const kept = rows.filter((r) => isKeptOnRegenerate(r, today));
  const upcoming = kept.filter((r) => r.session_date >= today);
  const uniqueDates = (list) => [...new Set(list.map((r) => r.session_date))].sort();
  const cancelledDates = uniqueDates(upcoming.filter((r) => r.cancelled_ind));
  const markedDates = uniqueDates(
    upcoming.filter((r) => !r.cancelled_ind && r.attendance_count > 0)
  ).filter((d) => !cancelledDates.includes(d));
  const blocked = new Set(upcoming.map((r) => r.session_date));
  return {
    rows: sortSessions([
      ...kept,
      ...generated.filter((g) => g.session_date >= today && !blocked.has(g.session_date)),
    ]),
    cancelledDates,
    markedDates,
  };
}

// Today as YYYY-MM-DD in the branch's timezone (an IANA name, e.g.
// "America/Toronto") when the client has it, else in the browser's. The
// server decides by the branch's calendar day; this only lets the portal say
// so before a save, and the server's refusal stays the backstop.
export function todayIn(timezone) {
  if (timezone) {
    try {
      // en-CA formats dates as YYYY-MM-DD.
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
    } catch (e) {
      // An unknown zone name: fall back to the browser's day.
    }
  }
  return moment().format("YYYY-MM-DD");
}

// "Fri, Sep 25, 2026"
export const formatSessionDate = (date) => moment(date, "YYYY-MM-DD").format("ddd, MMM D, YYYY");

// "6:00 PM–7:30 PM"
export const formatSessionTimes = (start, end) =>
  [start, end]
    .filter(Boolean)
    .map((t) => moment(t, "HH:mm").format("h:mm A"))
    .join("–");

// The server's 400 checks, made before the PUT so staff see every problem at
// once: inside the event's dates, end after start, no two sessions starting
// at the same date and time, a note of at most 200 characters.
export function validateSessionsDefinition(rows, { startdate, enddate }) {
  const errors = [];
  const slots = new Set();
  sortSessions(rows).forEach((s) => {
    const name = s.session_date ? formatSessionDate(s.session_date) : "A session";
    if (!moment(s.session_date, "YYYY-MM-DD", true).isValid()) {
      errors.push(`${name}: enter the date`);
      return;
    }
    if (s.session_date < startdate || s.session_date > enddate)
      errors.push(`${name}: outside the event's dates (${startdate} to ${enddate})`);
    if (!s.start_time || !s.end_time) errors.push(`${name}: enter the start and end times`);
    else if (s.end_time <= s.start_time)
      errors.push(`${name}: the end time must be after the start time`);
    const slot = `${s.session_date} ${s.start_time}`;
    if (s.start_time && slots.has(slot))
      errors.push(`${name}: two sessions start at ${formatSessionTimes(s.start_time)}`);
    slots.add(slot);
    if (s.note.trim().length > 200) errors.push(`${name}: the note is over 200 characters`);
  });
  return errors;
}
